/**
 * Mixpanel Analytics Integration
 *
 * Comprehensive event tracking and user analytics with:
 * - GDPR consent integration (Axeptio)
 * - Proper anonymous-to-identified user transition (alias)
 * - Event queue for offline/failed events
 * - UTM parameter persistence
 * - Development validation and debug mode
 *
 * @version 2.0.0 - November 2025
 * @see https://docs.mixpanel.com/docs/tracking-methods/sdks/javascript
 */

import type {Dict} from 'mixpanel-browser';
import {env} from '@/shared/config/environment';
import {logger} from '@/shared/lib/logger/logger';

// =============================================================================
// TYPES
// =============================================================================

interface MixpanelPeople {
  set(properties: Record<string, unknown>): void;
  set_once(properties: Record<string, unknown>): void;
  increment(property: string, amount?: number): void;
  track_charge(amount: number, properties?: Record<string, unknown>): void;
}

interface MixpanelConfig {
  api_host?: string;
  autocapture?: boolean;
  record_sessions_percent?: number;
  persistence?: string;
  track_pageview?: boolean;
  opt_out_tracking_by_default?: boolean;
  ignore_dnt?: boolean;
  debug?: boolean;
  verbose?: boolean;
  loaded?: () => void;
}

interface MixpanelInstance {
  init(token: string, config?: MixpanelConfig): void;
  track(eventName: string, properties?: Dict): void;
  identify(userId: string): void;
  alias(alias: string, original?: string): void;
  reset(): void;
  time_event(eventName: string): void;
  register(properties: Dict): void;
  register_once(properties: Dict): void;
  opt_in_tracking(): void;
  opt_out_tracking(): void;
  has_opted_in_tracking(): boolean;
  has_opted_out_tracking(): boolean;
  set_config(config: MixpanelConfig): void;
  get_distinct_id(): string | undefined;
  people: MixpanelPeople;
  __loaded?: boolean;
}

declare global {
  interface Window {
    mixpanel: MixpanelInstance | undefined;
  }
}

// Type definitions for common event properties
interface UserProperties extends Record<string, unknown> {
  email: string;
  name?: string;
  createdAt: string;
  subscriptionStatus?: string;
  totalOrders?: number;
  totalSpent?: number;
  preferredLanguage?: string;
  country?: string;
}

interface PageViewProperties {
  path: string;
  referrer: string;
  search: string;
  title: string;
}

interface CheckoutProperties {
  orderId?: string;
  totalAmount: number;
  currency: string;
  itemCount: number;
  hasAddOns: boolean;
  paymentMethod?: string;
  country?: string;
  vatAmount?: number;
  subtotal?: number;
}

// =============================================================================
// STATE MANAGEMENT
// =============================================================================

/** Event queue for failed/offline events */
const eventQueue: { name: string; props: Dict }[] = [];
let isProcessingQueue = false;
let isInitialized = false;
let hasUserConsented = false;

// =============================================================================
// INITIALIZATION
// =============================================================================

/**
 * Initialize Mixpanel with proper consent handling
 *
 * Features:
 * - Token from environment variable (not hardcoded)
 * - GDPR-compliant: starts opted-out by default
 * - Listens for Axeptio consent before enabling tracking
 * - Uses EU API host for GDPR compliance
 */
export function initMixpanel(): void {
  const { mixpanel: config } = env;

  // Only proceed if enabled and token exists
  if (!config.enabled || !config.token) {
    logger.debug('[Mixpanel] Not initialized - disabled or missing token', {
      component: 'Mixpanel',
      action: 'init',
    });
    return;
  }

  if (typeof window === 'undefined' || !window.mixpanel) {
    logger.debug('[Mixpanel] SDK not available', {
      component: 'Mixpanel',
      action: 'init',
    });
    return;
  }

  try {
    // Check for existing consent (returning visitor)
    hasUserConsented = hasExistingConsent();

    // Initialize Mixpanel with token from environment
    window.mixpanel.init(config.token, {
      api_host: 'https://api-eu.mixpanel.com', // GDPR: EU data residency
      autocapture: hasUserConsented, // Only if already consented
      record_sessions_percent: hasUserConsented ? 100 : 0,
      persistence: 'localStorage',
      track_pageview: false, // We handle page views manually
      ignore_dnt: true, // Ignore Do Not Track browser setting
      opt_out_tracking_by_default: !hasUserConsented, // GDPR: Start opted out
      debug: env.isDevelopment && hasUserConsented, // Debug logs only if consented (reduces noise)
      verbose: env.isDevelopment && hasUserConsented,
      loaded: () => {
        isInitialized = true;
        logger.info('[Mixpanel] SDK loaded and initialized');

        if (hasUserConsented) {
          // Safety check: ensure opt_in_tracking is a function (SDK may still be initializing)
          if (typeof window.mixpanel?.opt_in_tracking === 'function') {
            window.mixpanel.opt_in_tracking();
            registerSuperProperties();
            captureUTMParameters();
            processQueue();
          } else {
            // Retry after a short delay if SDK isn't fully ready
            setTimeout(() => {
              if (typeof window.mixpanel?.opt_in_tracking === 'function') {
                window.mixpanel.opt_in_tracking();
                registerSuperProperties();
                captureUTMParameters();
                processQueue();
              }
            }, 100);
          }
        }
      },
    });

    // Set up consent listener
    setupConsentListener();

  } catch (error) {
    logger.error('[Mixpanel] Initialization error:', error instanceof Error ? error : new Error(String(error)));
  }
}

/**
 * Set up listener for Axeptio consent changes
 * In development, auto-enable tracking without waiting for consent
 */
function setupConsentListener(): void {
  if (typeof window === 'undefined') return;

  // In development, auto-enable tracking
  if (env.isDevelopment) {
    if (!hasUserConsented) {
      hasUserConsented = true;
      enableTracking();
      logger.info('[Mixpanel] Development mode - tracking auto-enabled (consent bypassed)');
    }
    return;
  }

  // Listen for consent changes from Axeptio (production only)
  window.addEventListener('axeptio:complete', ((event: CustomEvent<{ vendors?: Record<string, boolean> }>) => {
    const vendors = event.detail.vendors;
    const consent = vendors?.['mixpanel'] ?? vendors?.['analytics_storage'];

    if (consent && !hasUserConsented) {
      hasUserConsented = true;
      enableTracking();
      logger.info('[Mixpanel] User consented - tracking enabled');
    } else if (!consent && hasUserConsented) {
      hasUserConsented = false;
      disableTracking();
      logger.info('[Mixpanel] User revoked consent - tracking disabled');
    }
  }) as EventListener);

  // Also listen for the Axeptio cookies_complete event (alternative event name)
  window.addEventListener('axeptio_on_consent', ((event: CustomEvent<{ analytics?: boolean }>) => {
    if (event.detail.analytics && !hasUserConsented) {
      hasUserConsented = true;
      enableTracking();
    }
  }) as EventListener);
}

/**
 * Check if user has existing consent (returning visitor)
 * In development, always return true to bypass consent
 */
function hasExistingConsent(): boolean {
  if (typeof window === 'undefined') return false;

  // In development, bypass consent checks
  if (env.isDevelopment) {
    return true;
  }

  try {
    // Check Axeptio consent cookie
    const consent = localStorage.getItem('axeptio_cookies');
    if (consent) {
      const parsed = JSON.parse(consent) as Record<string, boolean>;
      return parsed['mixpanel'] === true || parsed['analytics_storage'] === true;
    }
  } catch {
    // Ignore parsing errors
  }
  return false;
}

/**
 * Enable tracking after consent
 */
function enableTracking(): void {
  if (!window.mixpanel) return;

  window.mixpanel.opt_in_tracking();
  window.mixpanel.set_config({
    autocapture: true,
    record_sessions_percent: 100,
  });
  registerSuperProperties();
  captureUTMParameters();
  processQueue();

  // Initialize conversion pixels after consent
  initializePixels();
}

/**
 * Initialize conversion pixels after user consent
 */
function initializePixels(): void {
  if (!env.pixels) return;

  // Dynamic import to avoid loading pixels code if not needed
  import('@/shared/marketing/pixels').then(({ initPixels }) => {
    const config: {
      facebookPixelId?: string;
      googleAdsConversionId?: string;
      googleAdsConversionLabel?: string;
    } = {};
    if (env.pixels?.facebookPixelId) config.facebookPixelId = env.pixels.facebookPixelId;
    if (env.pixels?.googleAdsConversionId) config.googleAdsConversionId = env.pixels.googleAdsConversionId;
    if (env.pixels?.googleAdsConversionLabel) config.googleAdsConversionLabel = env.pixels.googleAdsConversionLabel;
    initPixels(config);
  }).catch((error) => {
    logger.error('[Mixpanel] Failed to initialize pixels', error instanceof Error ? error : new Error(String(error)), {
      component: 'Mixpanel',
      action: 'init_pixels',
    });
  });
}

/**
 * Disable tracking when consent revoked
 */
function disableTracking(): void {
  if (!window.mixpanel) return;

  window.mixpanel.opt_out_tracking();
  window.mixpanel.set_config({
    autocapture: false,
    record_sessions_percent: 0,
  });
}

/**
 * Register super properties (sent with every event)
 */
function registerSuperProperties(): void {
  if (typeof window === 'undefined' || !window.mixpanel?.register) return;

  window.mixpanel.register({
    environment: env.environment,
    app_version: getAppVersion(),
    platform: 'web',
    app_name: 'Tangi',
  });
}

/**
 * Get app version dynamically
 */
function getAppVersion(): string {
  // Try Vite define, then env var, then fallback
  if (typeof __APP_VERSION__ !== 'undefined') {
    return __APP_VERSION__;
  }
  return import.meta.env.VITE_APP_VERSION ?? '1.0.0';
}

/**
 * Capture and persist UTM parameters as super properties
 */
function captureUTMParameters(): void {
  if (typeof window === 'undefined' || !window.mixpanel?.register_once) return;

  const params = new URLSearchParams(window.location.search);
  const utmParams: Record<string, string> = {};

  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  utmKeys.forEach((key) => {
    const value = params.get(key);
    if (value) {
      utmParams[key] = value;
    }
  });

  // Also capture referrer for first-touch attribution
  if (document.referrer && !utmParams['referrer']) {
    utmParams['initial_referrer'] = document.referrer;
  }

  if (Object.keys(utmParams).length > 0) {
    // register_once ensures we keep first-touch attribution
    window.mixpanel.register_once(utmParams);
    logger.debug('[Mixpanel] UTM parameters captured', {
      component: 'Mixpanel',
      action: 'capture_utm',
      utmParams,
    });
  }
}

// =============================================================================
// CORE TRACKING
// =============================================================================

/**
 * Check if Mixpanel is enabled and user has consented
 *
 * Also verifies that the SDK is fully loaded (not just the stub)
 * by checking that .track is a function.
 */
function isMixpanelEnabled(): boolean {
  if (typeof window === 'undefined' || !window.mixpanel) return false;

  // Verify SDK is fully loaded (not just the stub)
  // The stub creates window.mixpanel but methods may not be functions during SDK replacement
  if (typeof window.mixpanel.track !== 'function') return false;

  return Boolean(env.mixpanel.enabled && isInitialized && hasUserConsented);
}

/**
 * Validate event properties in development
 */
function validateEventInDev(eventName: string, properties: Dict): void {
  if (!env.isDevelopment) return;

  // Check for undefined values
  Object.entries(properties).forEach(([key, value]) => {
    if (value === undefined) {
      logger.warn(`[Mixpanel] Undefined value for property "${key}" in event "${eventName}"`, {
        component: 'Mixpanel',
        action: 'validate_event',
        eventName,
        property: key,
      });
    }
  });

  // Check for overly long strings (Mixpanel has 255 char limit for property values)
  Object.entries(properties).forEach(([key, value]) => {
    if (typeof value === 'string' && value.length > 255) {
      logger.warn(`[Mixpanel] String too long (${value.length} chars) for "${key}" in "${eventName}" - will be truncated`, {
        component: 'Mixpanel',
        action: 'validate_event',
        eventName,
        property: key,
        valueLength: value.length,
      });
    }
  });

  // Check for reserved property names
  const reserved = ['$distinct_id', '$user_id', '$insert_id', 'token', 'mp_'];
  Object.keys(properties).forEach((key) => {
    if (reserved.some(r => key.startsWith(r))) {
      logger.warn(`[Mixpanel] Property "${key}" uses reserved prefix in "${eventName}"`, {
        component: 'Mixpanel',
        action: 'validate_event',
        eventName,
        property: key,
      });
    }
  });
}

/**
 * Process queued events when Mixpanel becomes available
 */
function processQueue(): void {
  if (isProcessingQueue || !isMixpanelEnabled() || eventQueue.length === 0) return;

  // Extra safety check: ensure track is a function
  if (typeof window.mixpanel?.track !== 'function') {
    logger.debug('[Mixpanel] SDK not ready for queue processing', {
      component: 'Mixpanel',
      action: 'process_queue',
    });
    return;
  }

  isProcessingQueue = true;
  logger.debug(`[Mixpanel] Processing ${eventQueue.length} queued events`, {
    component: 'Mixpanel',
    action: 'process_queue',
    queueLength: eventQueue.length,
  });

  while (eventQueue.length > 0) {
    const event = eventQueue.shift();
    if (event) {
      try {
        window.mixpanel.track(event.name, event.props);
      } catch {
        // Re-queue on failure
        eventQueue.unshift(event);
        break;
      }
    }
  }

  isProcessingQueue = false;
}

/**
 * Track a generic event
 * - Queues events if Mixpanel not ready
 * - Validates properties in development
 * - Does NOT add timestamp (handled by enrichment in useAnalytics)
 */
export function trackEvent(eventName: string, properties?: Dict): void {
  const props = properties ?? {};

  // Validate in development
  validateEventInDev(eventName, props);

  // If not enabled, queue for later
  if (!isMixpanelEnabled()) {
    // Only queue if we have consent but SDK not ready yet
    if (hasUserConsented && !isInitialized) {
      eventQueue.push({ name: eventName, props });
      logger.debug(`[Mixpanel] Event queued: ${eventName}`, {
        component: 'Mixpanel',
        action: 'queue_event',
        eventName,
      });
    }
    return;
  }

  try {
    // Double-check that track is a function (SDK may be in transition state)
    if (typeof window.mixpanel?.track !== 'function') {
      // Queue for later when SDK is ready
      eventQueue.push({ name: eventName, props });
      logger.debug(`[Mixpanel] SDK not ready, event queued: ${eventName}`, {
        component: 'Mixpanel',
        action: 'queue_event',
        eventName,
      });
      return;
    }

    window.mixpanel.track(eventName, props);
    logger.debug(`[Mixpanel] Event tracked: ${eventName}`, {
      component: 'Mixpanel',
      action: 'track_event',
      eventName,
      props,
    });
  } catch (error) {
    logger.error('[Mixpanel] Error tracking event', error instanceof Error ? error : new Error(String(error)), {
      component: 'Mixpanel',
      action: 'track_event',
      eventName,
    });
    // Queue for retry
    eventQueue.push({ name: eventName, props });
  }
}

/**
 * Track page views
 */
export function trackPageView(properties: PageViewProperties): void {
  if (!isMixpanelEnabled()) return;

  trackEvent('Page Viewed', {
    ...properties,
    url: window.location.href,
  });
}

// =============================================================================
// USER IDENTIFICATION
// =============================================================================

/**
 * Identify a user with Mixpanel
 *
 * Properly handles anonymous-to-identified user transition:
 * 1. Gets current distinct_id (anonymous ID)
 * 2. Creates alias to merge anonymous profile with identified user
 * 3. Identifies user with new ID
 * 4. Sets user properties
 */
export function identifyUser(userId: string, properties?: UserProperties): void {
  if (!isMixpanelEnabled() || typeof window === 'undefined' || !window.mixpanel) return;

  try {
    // Get current distinct_id before identifying (this is the anonymous ID)
    const currentDistinctId = window.mixpanel.get_distinct_id();

    // If transitioning from anonymous to identified, create alias first
    // This merges the anonymous profile data with the identified user
    if (
      currentDistinctId &&
      currentDistinctId !== userId &&
      !currentDistinctId.startsWith('$device:') // Already device ID
    ) {
      window.mixpanel.alias(userId, currentDistinctId);
      logger.debug('[Mixpanel] Created alias', {
        component: 'Mixpanel',
        action: 'alias',
        currentDistinctId,
        userId,
      });
    }

    // Now identify with the user ID
    window.mixpanel.identify(userId);

    if (properties) {
      // Use set_once for properties that shouldn't change (first values)
      window.mixpanel.people.set_once({
        $email: properties.email,
        $created: properties.createdAt,
      });

      // Use set for properties that can be updated
      window.mixpanel.people.set({
        $name: properties.name,
        subscription_status: properties.subscriptionStatus,
        preferred_language: properties.preferredLanguage,
        country: properties.country,
      });
    }

    logger.debug('[Mixpanel] User identified', {
      component: 'Mixpanel',
      action: 'identify',
      userId,
    });
  } catch (error) {
    logger.error('[Mixpanel] Error identifying user', error instanceof Error ? error : new Error(String(error)), {
      component: 'Mixpanel',
      action: 'identify',
      userId,
    });
  }
}

/**
 * Set user properties (profile data)
 */
export function setUserProperties(properties: UserProperties): void {
  if (!isMixpanelEnabled() || typeof window === 'undefined' || !window.mixpanel) return;

  try {
    window.mixpanel.people.set({
      $email: properties.email,
      $name: properties.name,
      subscription_status: properties.subscriptionStatus,
      total_orders: properties.totalOrders,
      total_spent: properties.totalSpent,
      preferred_language: properties.preferredLanguage,
      country: properties.country,
    });
    logger.debug('[Mixpanel] User properties set', {
      component: 'Mixpanel',
      action: 'set_user_properties',
      properties,
    });
  } catch (error) {
    logger.error('[Mixpanel] Error setting user properties', error instanceof Error ? error : new Error(String(error)), {
      component: 'Mixpanel',
      action: 'set_user_properties',
    });
  }
}

/**
 * Increment a user property (e.g., total orders)
 */
export function incrementUserProperty(property: string, amount = 1): void {
  if (!isMixpanelEnabled() || typeof window === 'undefined' || !window.mixpanel) return;

  try {
    window.mixpanel.people.increment(property, amount);
  } catch (error) {
    logger.error('[Mixpanel] Error incrementing user property', error instanceof Error ? error : new Error(String(error)), {
      component: 'Mixpanel',
      action: 'increment_user_property',
      property,
    });
  }
}

/**
 * Reset user identity (on logout)
 */
export function resetUser(): void {
  if (!isMixpanelEnabled() || typeof window === 'undefined' || !window.mixpanel) return;

  try {
    window.mixpanel.reset();
    logger.debug('[Mixpanel] User reset', {
      component: 'Mixpanel',
      action: 'reset',
    });
  } catch (error) {
    logger.error('[Mixpanel] Error resetting user', error instanceof Error ? error : new Error(String(error)), {
      component: 'Mixpanel',
      action: 'reset',
    });
  }
}

// =============================================================================
// AUTH TRACKING
// =============================================================================

/**
 * Track user signup
 */
export function trackSignUp(method: string, userId?: string): void {
  trackEvent('User Signed Up', {
    method,
    userId,
  });
}

/**
 * Track user login
 */
export function trackSignIn(method: string, userId?: string): void {
  trackEvent('User Signed In', {
    method,
    userId,
  });
}

/**
 * Track user logout
 */
export function trackSignOut(): void {
  trackEvent('User Signed Out');
}

// =============================================================================
// WIZARD TRACKING
// =============================================================================

// =============================================================================
// CHECKOUT TRACKING
// =============================================================================

/**
 * Track order completed with revenue
 */
export function trackOrderCompleted(properties: CheckoutProperties & { orderId: string }): void {
  trackEvent('Order Completed', properties);

  // Track revenue
  if (isMixpanelEnabled() && typeof window !== 'undefined' && window.mixpanel) {
    try {
      window.mixpanel.people.track_charge(properties.totalAmount, {
        $time: new Date().toISOString(),
        order_id: properties.orderId,
        currency: properties.currency,
      });
    } catch (error) {
      logger.error('[Mixpanel] Error tracking revenue', error instanceof Error ? error : new Error(String(error)), {
        component: 'Mixpanel',
        action: 'track_revenue',
        amount: properties.totalAmount,
      });
    }
  }
}


// =============================================================================
// TIMING
// =============================================================================

/**
 * Start timing an event
 */
export function startTiming(eventName: string): void {
  if (!isMixpanelEnabled() || typeof window === 'undefined' || !window.mixpanel) return;

  try {
    window.mixpanel.time_event(eventName);
  } catch (error) {
    logger.error('[Mixpanel] Error starting timing', error instanceof Error ? error : new Error(String(error)), {
      component: 'Mixpanel',
      action: 'time_event',
      eventName,
    });
  }
}


// =============================================================================
// EXPORTS
// =============================================================================

/**
 * Get the Mixpanel instance for advanced usage
 */
export const getMixpanel = (): MixpanelInstance | null => {
  return typeof window !== 'undefined' && window.mixpanel ? window.mixpanel : null;
};

/**
 * Check if user has given consent
 */
export const hasConsent = (): boolean => hasUserConsented;

/**
 * Check if Mixpanel is initialized
 */
export const isReady = (): boolean => isInitialized && hasUserConsented;
